import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FileService, IStorage, LocalStorage } from '@isophi/core-legacy';
import { IconService, OrderingService, ToastService } from '@isophi/core-ui';
import { ManagerTeacher, PaginatedManagerTeacherList, TeachersService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, map, Observable, switchMap, takeUntil, tap } from 'rxjs';

import { HelperService } from '../../../../core/services/helper.service';
import { BaseComponent } from '../../../../shared/components/base/base.component';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { PapTeacherResultsComponent } from '../../components/pap-teacher-results/pap-teacher-results.component';
import { AutoEvaluationStateEnum } from '../../constants/data';
import { AutoEvaluationFilterCriteria } from '../../constants/models';

@Component({
  selector: 'app-pap-autoevaluation',
  templateUrl: './pap-autoevaluation.component.html',
  styleUrls: ['./pap-autoevaluation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PapAutoEvaluationComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly helperService = inject(HelperService);
  private readonly translocoService = inject(TranslocoService);
  protected readonly iconService = inject(IconService);
  private readonly teacherService = inject(TeachersService);
  private readonly orderingService = inject(OrderingService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(NgbModal);
  private readonly fileService = inject(FileService);
  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly localStorage: IStorage = new LocalStorage();

  teacherList$: Observable<PaginatedManagerTeacherList>;

  private teachersReload$ = new BehaviorSubject<void>(null);

  private filterCriteria: AutoEvaluationFilterCriteria = {
    teacherId: null,
    state: null,
  };

  protected page = 1;
  protected limit = 10;
  protected offset = 0;
  protected ordering: string[] = [];

  ngOnInit(): void {
    this.helperService.setTitle(undefined, this.translocoService.translate('pap/autoEvaluation/title'));
    const saved = this.localStorage.getItem(this.lSConstService.papAutoevaluationPageLimit);
    if (saved) {
      this.limit = +saved;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.helperService.clearSmallTextTitle();
    super.ngOnDestroy();
  }

  loadData(): void {
    this.teacherList$ = this.teachersReload$.pipe(
      switchMap(() =>
        this.teacherService.teachersList(
          this.filterCriteria.teacherId,
          this.limit,
          this.offset,
          this.orderingService.getOrderingValue(this.ordering)
        )
      ),
      map((teacherList) => {
        return {
          ...teacherList,
          results: teacherList.results
            .map((teacher) => {
              if (!teacher.lastEvaluationDate) {
                return {
                  ...teacher,
                  nextEvaluationDate: null,
                  nextEvaluationWarningDate: null,
                  completed: false,
                };
              }

              const today = new Date();

              const nextEvaluationDate = new Date(teacher.lastEvaluationDate);
              nextEvaluationDate.setFullYear(nextEvaluationDate.getFullYear() + 1);

              const nextEvaluationWarningDate = new Date(teacher.lastEvaluationDate);
              nextEvaluationWarningDate.setMonth(nextEvaluationWarningDate.getMonth() + 11);

              return {
                ...teacher,
                nextEvaluationDate,
                nextEvaluationWarningDate,
                completed: today <= nextEvaluationWarningDate,
              };
            })
            .filter((teacher) => {
              switch (this.filterCriteria.state) {
                case AutoEvaluationStateEnum.COMPLETED:
                  return teacher.completed;
                case AutoEvaluationStateEnum.NOT_COMPLETED:
                  return !teacher.completed;
                default:
                  return true;
              }
            }),
        };
      }),
      tap((teacherList) => {
        const teacherText =
          teacherList.count > 4
            ? this.translocoService.translate('pap/autoEvaluation/autoEvaluations')
            : this.translocoService.translate('pap/autoEvaluation/autoEvaluation');
        if (teacherList.count > 0) {
          this.helperService.setSmallTextTitle(`${teacherList.count} ${teacherText}`);
        }
      })
    );
  }

  reloadTeachers(): void {
    this.teachersReload$.next();
  }

  onPageChange(page: number): void {
    this.offset = (page - 1) * this.limit;
    this.reloadTeachers();
  }

  onLimitChange(value: number): void {
    this.limit = value;
    this.page = 1;
    this.offset = 0;
    this.localStorage.setItem(this.lSConstService.papAutoevaluationPageLimit, String(value));
    this.reloadTeachers();
  }

  onOrderingChange(ordering: string[]): void {
    this.ordering = ordering;
    this.reloadTeachers();
  }

  exportPage(type: 'xlsx' | 'csv'): void {
    this.spinner.show();
    this.teacherService
      .teachersPrintRetrieve(this.filterCriteria.teacherId, this.orderingService.getOrderingValue(this.ordering), undefined, type)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (data: Blob) => {
          this.fileService.downloadFile(
            data,
            `${this.translocoService.translate('file/autoeval')}.${type}`,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          );
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('pap/autoEvaluation/exportErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  setFilter(filterCriteria: AutoEvaluationFilterCriteria): void {
    this.filterCriteria = filterCriteria;
    this.page = 1;
    this.offset = 0;
    this.reloadTeachers();
  }

  openResults(teacher: ManagerTeacher): void {
    const modalRef = this.modalService.open(PapTeacherResultsComponent, {
      size: 'xl',
    });
    modalRef.componentInstance.teacher = teacher;
  }
}
